import { useQuery } from "@tanstack/react-query";
import { fetchStats } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import { Package, Weight, Layers, ArrowUpRight, Loader2, Scissors, Truck } from "lucide-react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";

const Metric = ({ label, value, sub, icon: Icon, testId }) => (
  <div
    data-testid={testId}
    className="bg-white border border-slate-200 rounded-sm p-5 flex flex-col gap-4 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-sm"
  >
    <div className="flex items-center justify-between">
      <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">
        {label}
      </p>
      <Icon className="h-4 w-4 text-slate-400" />
    </div>
    <p className="font-mono-data text-3xl font-bold tracking-tight text-slate-900 leading-none">
      {value}
    </p>
    {sub && <p className="text-xs text-slate-500">{sub}</p>}
  </div>
);

const formatWeight = (kg) => {
  if (!kg) return "0";
  if (kg >= 1000) return (kg / 1000).toFixed(2);
  return kg.toFixed(0);
};

const Dashboard = () => {
  const { user } = useAuth();
  const isExec = !!user?.exec_dashboard;
  const { data, isLoading, error } = useQuery({
    queryKey: ["stats"],
    queryFn: fetchStats,
    refetchInterval: 30_000,
    enabled: !isExec,
  });

  if (isExec) return <ExecutiveDashboard />;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="dashboard-error" className="bg-red-50 border border-red-200 rounded-sm p-6 text-sm text-red-800">
        Failed to load stats. Check that the backend is running.
      </div>
    );
  }

  const stats = data || {};
  const totalWeightTons = (stats.total_weight_kg || 0) / 1000;
  const totalGrades = (stats.by_grade || []).length;

  const gradeBars = (stats.by_grade || []).slice(0, 8).map((g, i) => ({
    name: g.grade,
    coils: g.count,
    weight: g.weight,
    fill: ["#0f172a", "#1e3a8a", "#1d4ed8", "#2563eb", "#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe"][i % 8],
  }));

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex items-end justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-1">Overview</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
            Inventory Control Room
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Real-time view of incoming steel coils across all bays.
          </p>
        </div>
        <Link
          to="/scan"
          data-testid="dashboard-scan-cta"
          className="hidden sm:inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 h-10 rounded-sm tracking-wide transition-colors"
        >
          Scan new coil <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric
          testId="metric-active-mothers"
          label="Active RM"
          value={stats.active_mother_count || 0}
          sub={`${formatWeight(stats.active_mother_weight_kg)} t`}
          icon={Package}
        />
        <Metric
          testId="metric-active-sfgs"
          label="Active SFG"
          value={stats.active_sfg_count || 0}
          sub={`${formatWeight(stats.active_sfg_weight_kg)} t`}
          icon={Scissors}
        />
        <Metric
          testId="metric-total-weight"
          label="Total Active Weight"
          value={`${formatWeight(stats.total_weight_kg)} t`}
          sub={`${(stats.total_weight_kg || 0).toLocaleString()} kg in stock`}
          icon={Weight}
        />
        <Metric
          testId="metric-dispatched"
          label="Dispatched (lifetime)"
          value={stats.dispatched_count || 0}
          sub="Marked outbound"
          icon={Truck}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric
          testId="metric-total-coils"
          label="Total Active Items"
          value={stats.total_count || 0}
          sub="RM + SFG"
          icon={Package}
        />
        <Metric
          testId="metric-pending"
          label="Pending (in transit)"
          value={stats.pending_count || 0}
          sub="Awaiting gate scan"
          icon={Package}
        />
        <Metric
          testId="metric-grades"
          label="Distinct Grades"
          value={totalGrades}
          sub="Active inventory"
          icon={Layers}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Grade breakdown */}
        <div data-testid="grade-chart-card" className="lg:col-span-3 bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">By Grade</p>
              <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5">Coil count per grade</h3>
            </div>
          </div>
          <div className="h-72">
            {gradeBars.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                No data yet — scan your first coil to populate.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={gradeBars} margin={{ top: 10, right: 10, left: -20, bottom: 30 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="0" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#475569" }}
                    angle={-25}
                    textAnchor="end"
                    height={50}
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: "rgba(15,23,42,0.05)" }}
                    contentStyle={{
                      background: "#0f172a",
                      border: "none",
                      borderRadius: 4,
                      fontSize: 12,
                      color: "white",
                    }}
                    labelStyle={{ color: "#94a3b8", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}
                  />
                  <Bar dataKey="coils" radius={[2, 2, 0, 0]}>
                    {gradeBars.map((entry) => (
                      <Cell key={entry.grade} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Recent additions */}
        <div data-testid="recent-additions-card" className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-5">
          <div className="mb-4">
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Recent</p>
            <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5">Latest additions</h3>
          </div>
          {(stats.recent || []).length === 0 ? (
            <div className="h-40 flex items-center justify-center text-sm text-slate-500">
              No coils yet.
            </div>
          ) : (
            <ul className="divide-y divide-slate-200">
              {(stats.recent || []).map((c) => (
                <li
                  key={c.id}
                  data-testid={`recent-coil-${c.coil_id}`}
                  className="py-3 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="font-mono-data text-sm font-semibold text-slate-900 truncate">
                      {c.coil_id}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {c.grade} · {c.supplier}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-mono-data text-sm text-slate-900">
                      {(c.weight_kg / 1000).toFixed(2)} t
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">
                      {c.date_received}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
